// src/pages/RecordDetailPage.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { RecordDetail } from '../features/records';
import RecordEditor from '../features/records/components/RecordEditor';
import ShareDialog from '../features/records/components/ShareDialog';
import Page from '../components/Page';
import { useDeleteRecord } from '../features/records/hooks/useRecordMutations';
import mapRecordsErrorToKey from '../features/records/utils/recordsErrorMapper';
import useUser from '../hooks/useUser';
import { useToast } from '../hooks/useToast';
import ConfirmationModal from '../components/ConfirmationModal';
import { sanitizeText } from '../utils/sanitize';
import styles from './RecordDetailPage.module.css';

const RecordDetailPage = () => {
  const { t } = useTranslation();
  const { recordId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: userLoading } = useUser();
  const toast = useToast();
  const deleteRecord = useDeleteRecord();
  
  const [scrolled, setScrolled] = useState(false);
  const [editorState, setEditorState] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [sharingRecord, setSharingRecord] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const editorKeyRef = useRef(0);
  const { openCommentsOnLoad, focusTarget } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const commentsValue = params.get('comments');
    const rawCommentId = params.get('commentId');
    const rawThreadRootId = params.get('threadRootId');
    const commentId = typeof rawCommentId === 'string' && rawCommentId.trim() ? rawCommentId.trim() : null;
    const threadRootId =
      typeof rawThreadRootId === 'string' && rawThreadRootId.trim() ? rawThreadRootId.trim() : null;
    const resolvedFocusTarget = commentId || threadRootId
      ? {
          commentId: commentId || threadRootId,
          threadRootId: threadRootId || commentId
        }
      : null;
    return {
      openCommentsOnLoad:
        commentsValue === '1' || commentsValue === 'true' || Boolean(resolvedFocusTarget),
      focusTarget: resolvedFocusTarget
    };
  }, [location.search]);

  // Handle scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (userLoading) return;
    
    if (!user) {
      navigate('/login', { state: { from: location } });
      return;
    }
  }, [user, userLoading, navigate, location]);

  const handleBackClick = () => {
    if (location.key !== 'default') {
      navigate(-1);
    } else {
      navigate('/records');
    }
  };

  const nextEditorKey = useCallback(() => {
    editorKeyRef.current += 1;
    return editorKeyRef.current;
  }, []);

  const openEditor = useCallback((record, initialLinks) => {
    setEditorState({
      key: nextEditorKey(),
      record: record ?? null,
      initialLinks
    });
  }, [nextEditorKey]);

  const handleEdit = (recordData) => {
    openEditor(recordData);
  };

  const handleDelete = (recordToDelete) => {
    // RecordDetail will pass the full record object
    if (recordToDelete) {
      setDeleteConfirmation(recordToDelete);
    }
  };

  const handleShare = (recordToShare) => {
    // RecordDetail will pass the full record object
    if (recordToShare) {
      setSharingRecord(recordToShare);
    }
  };

  const confirmDelete = async () => {
    const recordToDelete = deleteConfirmation;
    setDeleteConfirmation(null);

    if (!recordToDelete) return;

    // Set deleting state before starting deletion
    setIsDeleting(true);

    // Use mutation hook for deleting
    deleteRecord.mutate(recordToDelete.id, {
      onSuccess: () => {
        toast.success(t('records.detail.toasts.deleted_success', 'Record deleted successfully'));
        navigate('/records');
      },
      onError: (error) => {
        console.error('Error deleting record:', error);

        // Reset deleting state on error
        setIsDeleting(false);

        const errorKey = mapRecordsErrorToKey(error, {
          fallbackKey: 'records.detail.errors.delete_failed'
        });
        toast.error(t(errorKey));
      }
    });
  };

  const cancelDelete = () => {
    setDeleteConfirmation(null);
  };

  const handleSaveRecord = () => {
    setEditorState(null);
  };

  const handleLinkToRecord = (targetRecordId) => {
    openEditor(null, [
      {
        targetId: targetRecordId,
        targetType: 'record',
        addedAt: new Date()
      }
    ]);
  };

  if (userLoading) {
    return (
      <Page width="default" className={styles.recordDetailPage}>
        <header className={`${styles.header} full-bleed`}>
          <button
            type="button"
            className={styles.backButton}
            aria-label={t('common.back', 'Back')}
            onClick={handleBackClick}
          >
            <ArrowLeft size={20} />
          </button>
        </header>
        <main className={styles.content} role="status" aria-live="polite">
          <div className={styles.loadingContainer}>
            <div className={styles.skeletonHeader}>
              <div className={styles.skeletonAvatar} />
              <div className={styles.skeletonMeta}>
                <div className={styles.skeletonName} />
                <div className={styles.skeletonDate} />
              </div>
            </div>
            <div className={styles.skeletonContent}>
              <div className={styles.skeletonTitle} />
              <div className={styles.skeletonText} />
              <div className={styles.skeletonText} style={{ width: '80%' }} />
              <div className={styles.skeletonText} style={{ width: '60%' }} />
            </div>
          </div>
        </main>
      </Page>
    );
  }

  return (
    <Page width="default" className={styles.recordDetailPage}>
      <header className={`${styles.header} full-bleed ${scrolled ? styles.headerScrolled : ''}`}>
        <button
          type="button"
          onClick={handleBackClick}
          className={styles.backButton}
          aria-label={t('common.back', 'Back')}
        >
          <ArrowLeft size={20} />
        </button>
      </header>
      
      <main className={styles.content}>
        {isDeleting ? (
          // Show deleting state
          <div className={styles.loadingContainer}>
            <div className={styles.skeletonContent}>
              <div className={styles.skeletonTitle} />
              <p style={{ textAlign: 'center', marginTop: '1rem', color: 'var(--text-secondary)' }}>
                {t('records.detail.deleting', 'Deleting record...')}
              </p>
            </div>
          </div>
        ) : (
          <div className={styles.recordWrapper}>
            <RecordDetail
              recordId={recordId}
              mode="page"
              initialOpenComments={openCommentsOnLoad}
              initialFocusTarget={focusTarget}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onShare={handleShare}
              onLinkToRecord={handleLinkToRecord}
            />
          </div>
        )}
      </main>

      {/* Record Editor Modal */}
      {editorState && (
        <RecordEditor
          key={editorState.key}
          record={editorState.record}
          initialLinks={editorState.initialLinks}
          isOpen={!!editorState}
          onClose={() => setEditorState(null)}
          onSave={handleSaveRecord}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <ConfirmationModal
          isOpen={!!deleteConfirmation}
          onClose={cancelDelete}
          onConfirm={confirmDelete}
          title={t('records.detail.delete_confirm.title', 'Delete record')}
          message={t('records.detail.delete_confirm.message', {
            title: sanitizeText(deleteConfirmation?.content?.title || '')
              || t('records.detail.delete_confirm.fallback_title', 'this record'),
            defaultValue: 'Are you sure you want to delete "{{title}}"? This action cannot be undone.'
          })}
          confirmText={t('records.actions.delete', 'Delete')}
          cancelText={t('common.cancel', 'Cancel')}
        />
      )}

      {/* Share Dialog */}
      {sharingRecord && (
        <ShareDialog
          record={sharingRecord}
          onClose={() => setSharingRecord(null)}
        />
      )}
    </Page>
  );
};

export default RecordDetailPage;
